/**
 * FilterPill Component
 *
 * Displays an active filter as a chip/pill
 */

import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { FilterPillProps, FilterOperatorLabels } from '../types';

/**
 * FilterPill Component
 *
 * @param filter - The active filter to display
 * @param definition - Filter definition
 * @param locked - Whether this pill can be edited/removed
 * @param onRemove - Callback when remove is clicked
 * @param onClick - Callback when pill is clicked (for editing)
 */
export const FilterPill: React.FC<FilterPillProps> = ({
  filter,
  definition,
  locked = false,
  onRemove,
  onClick,
}) => {
  // Get display value
  const displayValue = definition.getDisplayValue
    ? definition.getDisplayValue(filter.value)
    : String(filter.value);

  // Get operator label
  const operatorLabel = FilterOperatorLabels[filter.operator] || filter.operator;

  // Determine if we should show the operator.
  //
  // The pill already reads "<shortLabel>: <value>", so an operator whose label
  // repeats the short label just stutters. Both filters that use `has` set
  // shortLabel to "Has", which rendered "Has: has Western Notes"; hiding it
  // gives "Has: Western Notes". Same reasoning as `is` / `equals`.
  const HIDDEN_OPERATORS = ['is', 'equals', 'has'];
  const showOperator = !HIDDEN_OPERATORS.includes(filter.operator);

  // The accessible name is built from the same pieces as the visible pill, so
  // a screen reader hears exactly what is on screen rather than "Has has ...".
  const accessibleName = [
    'Filter:',
    definition.shortLabel,
    showOperator ? operatorLabel : null,
    displayValue,
    locked ? '(locked)' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!locked && onClick) {
      onClick();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!locked && onRemove) {
      onRemove();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!locked && onClick) {
        onClick();
      }
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (!locked && onRemove) {
        onRemove();
      }
    }
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm
        ${locked
          ? 'bg-bg-3/60 text-text-tertiary border border-light/50'
          : 'bg-accent-indigo/15 text-text-primary border border-accent-indigo/30 cursor-pointer hover:bg-accent-indigo/25'
        }
        transition-colors duration-150
      `}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={locked ? -1 : 0}
      role="button"
      aria-label={accessibleName}
    >
      {/* Filter label */}
      <span className="font-medium text-accent-gold">
        {definition.shortLabel}:
      </span>

      {/* Operator (if shown) */}
      {showOperator && (
        <span className="text-text-tertiary">
          {operatorLabel}
        </span>
      )}

      {/* Value */}
      <span className="max-w-[150px] truncate text-text-primary">
        {displayValue}
      </span>

      {/* Remove button */}
      {!locked && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          className="
            ml-0.5 p-0.5 rounded-full
            text-text-secondary
            hover:bg-accent-indigo/40 hover:text-text-primary
            focus:outline-none focus:ring-2 focus:ring-accent-gold/40
            transition-colors duration-150
          "
          aria-label={`Remove ${definition.shortLabel} filter`}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
};

export default FilterPill;
